import React, { useEffect, useState } from 'react';
import { Card, Button, Tag, Table, Space, Modal, Radio, Statistic, Row, Col, message, Descriptions, Alert } from 'antd';
import { CrownOutlined, CheckCircleOutlined, RocketOutlined, ThunderboltOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';
import { HelpTooltipInline } from '../help/HelpButton';

interface Plan {
  id: string; name: string;
  monthly_price: number; yearly_price: number;
  max_products: number; max_orders_per_month: number; max_admins: number;
  features: string[];
}

interface SubInfo {
  tenant_id: number; tenant_name: string; current_plan: string; plan_name: string;
  plan_price?: number;
  is_active?: boolean; days_remaining?: number; expires_at?: string | null;
  plan_limits: { max_products: number; max_orders_per_month: number; max_admins: number };
  features: string[];
  subscription: { id: number; status: string; period: string; price: number;
    current_period_start: string; current_period_end: string;
    trial_ends_at: string; cancelled_at: string } | null;
  trial_ends_at: string | null;
}

interface Invoice {
  id: number; invoice_number: string; status: string; amount: number;
  currency: string; description: string; paid_at: string;
  period_start: string; period_end: string; created_at: string;
}

const PLAN_ICONS: Record<string, React.ReactNode> = {
  free: <CheckCircleOutlined />,
  starter: <RocketOutlined />,
  business: <ThunderboltOutlined />,
  enterprise: <CrownOutlined />,
  pro: <CrownOutlined />,
};
const PLAN_COLORS: Record<string, string> = {
  free: '#8c8c8c', starter: '#1890ff', business: '#722ed1', enterprise: '#d4af37',
  pro: '#d4af37',
};
const STATUS_COLORS: Record<string, string> = {
  active: 'green', trial: 'blue', past_due: 'orange', cancelled: 'red', expired: 'default',
  paid: 'green', pending: 'processing', failed: 'error', draft: 'default',
};

export default function BillingPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [sub, setSub] = useState<SubInfo | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [subscribing, setSubscribing] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [plansRes, subRes, invRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/subscription'),
        api.get('/billing/invoices'),
      ]);
      setPlans(plansRes.data);
      setSub(subRes.data);
      setInvoices(invRes.data);
    } catch { message.error('Ошибка загрузки'); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSubscribe = async () => {
    if (!selectedPlan) return;
    setSubscribing(true);
    try {
      const { data } = await api.post('/billing/subscribe', { plan: selectedPlan, period });
      if (data.payment_url) { window.location.href = data.payment_url; return; }
      message.success(data.message);
      setUpgradeModal(false);
      fetchAll();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка');
    }
    setSubscribing(false);
  };

  const handlePayNow = async (p: 'monthly' | 'yearly' = 'monthly') => {
    const planId = plans[0]?.id || 'pro';
    setSubscribing(true);
    try {
      const { data } = await api.post('/billing/subscribe', { plan: planId, period: p });
      if (data.payment_url) { window.location.href = data.payment_url; return; }
      message.success(data.message);
      fetchAll();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка');
    }
    setSubscribing(false);
  };

  const handleCancel = () => {
    Modal.confirm({
      title: 'Отменить подписку?',
      content: 'Доступ сохранится до конца оплаченного периода. После этого тариф станет Free.',
      okText: 'Отменить подписку',
      okType: 'danger',
      cancelText: 'Не отменять',
      onOk: async () => {
        try {
          const { data } = await api.post('/billing/cancel', {});
          message.success(data.message);
          fetchAll();
        } catch (e: any) {
          message.error(e.response?.data?.detail || 'Ошибка');
        }
      },
    });
  };

  const invColumns = [
    { title: '№', dataIndex: 'invoice_number', key: 'num', width: 160 },
    { title: 'Описание', dataIndex: 'description', key: 'desc' },
    { title: 'Сумма', dataIndex: 'amount', key: 'amount', render: (v: number) => `${v.toLocaleString()} ₽` },
    { title: 'Статус', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={STATUS_COLORS[s]}>{s.toUpperCase()}</Tag> },
    { title: 'Дата', dataIndex: 'created_at', key: 'date',
      render: (d: string) => d ? dayjs(d).format('DD.MM.YYYY') : '—' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>💳 Подписка и биллинг <HelpTooltipInline sectionId="billing" /></h2>

      {/* Current plan card */}
      {sub && (
        <Card style={{ marginBottom: 24 }}>
          {/* Отсчёт дней / статус оплаты */}
          {sub.is_active === false ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Подписка истекла"
              description="Приём заказов на витрине приостановлен. Оплатите подписку, чтобы возобновить работу."
              action={
                <Button type="primary" danger loading={subscribing} onClick={() => handlePayNow('monthly')}>
                  Оплатить {sub.plan_price ? `${sub.plan_price.toLocaleString()} ₽` : ''}
                </Button>
              }
            />
          ) : typeof sub.days_remaining === 'number' && sub.days_remaining <= 5 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`До окончания подписки осталось ${sub.days_remaining} дн.`}
              description="Продлите подписку заранее, чтобы приём заказов не прерывался."
              action={
                <Button type="primary" loading={subscribing} onClick={() => handlePayNow('monthly')}>
                  Оплатить {sub.plan_price ? `${sub.plan_price.toLocaleString()} ₽` : ''}
                </Button>
              }
            />
          ) : null}

          <Row gutter={24}>
            <Col span={6}>
              <Statistic
                title="Текущий тариф"
                value={sub.plan_name}
                prefix={PLAN_ICONS[sub.current_plan]}
                valueStyle={{ color: PLAN_COLORS[sub.current_plan] }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Статус"
                value={sub.subscription?.status?.toUpperCase() || 'TRIAL'}
                valueStyle={{ color: STATUS_COLORS[sub.subscription?.status || 'trial'] === 'green' ? '#52c41a' : '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Осталось дней"
                value={typeof sub.days_remaining === 'number' ? sub.days_remaining : '—'}
                valueStyle={{ color: (sub.days_remaining ?? 99) <= 5 ? '#faad14' : '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              {sub.subscription?.current_period_end && (
                <Statistic
                  title="Оплачено до"
                  value={dayjs(sub.subscription.current_period_end).format('DD.MM.YYYY')}
                />
              )}
              {sub.trial_ends_at && !sub.subscription?.current_period_end && (
                <Statistic
                  title="Пробный период до"
                  value={dayjs(sub.trial_ends_at).format('DD.MM.YYYY')}
                />
              )}
            </Col>
          </Row>

          <Descriptions bordered column={3} size="small" style={{ marginTop: 16 }}>
            <Descriptions.Item label="Макс. товаров">
              {sub.plan_limits.max_products === -1 ? '∞' : sub.plan_limits.max_products}
            </Descriptions.Item>
            <Descriptions.Item label="Макс. заказов/мес">
              {sub.plan_limits.max_orders_per_month === -1 ? '∞' : sub.plan_limits.max_orders_per_month}
            </Descriptions.Item>
            <Descriptions.Item label="Макс. админов">
              {sub.plan_limits.max_admins === -1 ? '∞' : sub.plan_limits.max_admins}
            </Descriptions.Item>
          </Descriptions>

          <Space style={{ marginTop: 16 }}>
            <Button type="primary" icon={<CrownOutlined />} loading={subscribing} onClick={() => {
              setSelectedPlan(plans[0]?.id || 'pro');
              setUpgradeModal(true);
            }}>
              Оплатить подписку{sub.plan_price ? ` — ${sub.plan_price.toLocaleString()} ₽/мес` : ''}
            </Button>
            {sub.subscription?.status === 'active' && (
              <Button danger onClick={handleCancel}>Отменить подписку</Button>
            )}
          </Space>
        </Card>
      )}

      {/* Invoices */}
      <Card title="📋 История платежей">
        <Table
          dataSource={invoices}
          columns={invColumns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Нет платежей' }}
        />
      </Card>

      {/* Upgrade Modal */}
      <Modal
        title="Выберите тариф"
        open={upgradeModal}
        onCancel={() => setUpgradeModal(false)}
        footer={null}
        width={800}
      >
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Radio.Group value={period} onChange={(e) => setPeriod(e.target.value)} buttonStyle="solid">
            <Radio.Button value="monthly">Ежемесячно</Radio.Button>
            <Radio.Button value="yearly">Ежегодно (-17%)</Radio.Button>
          </Radio.Group>
        </div>

        <Row gutter={16}>
          {plans.map((plan) => {
            const price = period === 'yearly' ? plan.yearly_price : plan.monthly_price;
            const isCurrent = sub?.current_plan === plan.id;
            return (
              <Col span={6} key={plan.id}>
                <Card
                  hoverable={!isCurrent}
                  onClick={() => !isCurrent && setSelectedPlan(plan.id)}
                  style={{
                    border: selectedPlan === plan.id ? `2px solid ${PLAN_COLORS[plan.id]}` : isCurrent ? '2px solid #52c41a' : undefined,
                    opacity: isCurrent ? 0.7 : 1,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 28, color: PLAN_COLORS[plan.id] }}>{PLAN_ICONS[plan.id]}</div>
                    <h3>{plan.name}</h3>
                    {isCurrent && <Tag color="green">Текущий</Tag>}
                    <div style={{ fontSize: 24, fontWeight: 700, margin: '8px 0' }}>
                      {price === 0 ? 'Бесплатно' : `${price.toLocaleString()} ₽`}
                    </div>
                    {price > 0 && <div style={{ color: '#999', fontSize: 12 }}>/{period === 'yearly' ? 'год' : 'мес'}</div>}
                    <div style={{ textAlign: 'left', marginTop: 12, fontSize: 12 }}>
                      <div>📦 {plan.max_products === -1 ? '∞' : plan.max_products} товаров</div>
                      <div>📋 {plan.max_orders_per_month === -1 ? '∞' : plan.max_orders_per_month} заказов/мес</div>
                      <div>👥 {plan.max_admins === -1 ? '∞' : plan.max_admins} админов</div>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>

        {selectedPlan && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="primary" size="large" loading={subscribing} onClick={handleSubscribe}>
              Перейти на {plans.find(p => p.id === selectedPlan)?.name} — {
                period === 'yearly'
                  ? `${(plans.find(p => p.id === selectedPlan)?.yearly_price || 0).toLocaleString()} ₽/год`
                  : `${(plans.find(p => p.id === selectedPlan)?.monthly_price || 0).toLocaleString()} ₽/мес`
              }
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
